/**
 * @fileoverview Hook for handling touch interactions on donut charts.
 * Provides mobile Sheet and tablet popover support with single-click behavior.
 * @module shared/hooks/useDonutTouchInteraction
 */

import { type ReactElement, useState, useRef, useEffect, useCallback, type RefObject } from 'react'
import { Sector } from 'recharts'
import type { PieSectorDataItem } from 'recharts/types/polar/Pie'
import { useIsMobile, useIsBelowDesktop } from './useIsMobile'

/**
 * Renders a pie/donut slice without animation (for touch devices).
 * Use this as activeShape on tablets/mobile to prevent the slice from
 * disappearing while still allowing click interactions. Rendered via
 * recharts' Sector so the slice keeps its rounded corners.
 */
export function renderStaticActiveShape(props: unknown): ReactElement {
  const p = props as PieSectorDataItem
  return (
    <Sector
      cx={p.cx ?? 0}
      cy={p.cy ?? 0}
      innerRadius={p.innerRadius ?? 0}
      outerRadius={p.outerRadius ?? 0}
      startAngle={p.startAngle ?? 0}
      endAngle={p.endAngle ?? 0}
      fill={p.fill}
      cornerRadius={p.cornerRadius ?? 0}
      stroke="none"
    />
  )
}

/**
 * Anchor position for tablet popover
 */
export interface AnchorPosition {
  x: number
  y: number
}

/**
 * Return type for useDonutTouchInteraction hook
 */
export interface DonutTouchInteractionResult<T> {
  /** Whether viewport is mobile (<768px) */
  isMobile: boolean
  /** Whether viewport is tablet (768px - 1047px) */
  isTablet: boolean
  /** Whether viewport is below desktop (<1048px) */
  isBelowDesktop: boolean
  /** Selected slice for mobile Sheet */
  mobileSlice: T | null
  /** Selected slice for tablet popover */
  tabletSlice: T | null
  /** Anchor position for tablet popover (center of chart container) */
  tabletAnchor: AnchorPosition | null
  /** Ref to attach to chart container for click-outside detection */
  chartContainerRef: RefObject<HTMLDivElement | null>
  /** Handler to call when a slice is clicked */
  handleSliceClick: (slice: T) => void
  /** Clear mobile slice selection (for Sheet onOpenChange) */
  clearMobileSlice: () => void
  /** Clear tablet slice selection */
  clearTabletSlice: () => void
}

/**
 * Hook for handling touch interactions on donut/pie charts.
 *
 * On mobile: tracks selected slice for Sheet display
 * On tablet: tracks selected slice for popover display with click-outside closing
 * On desktop: returns null slices (use RechartsHoverAdapter instead)
 *
 * @example
 * ```tsx
 * const {
 *   isMobile,
 *   isTablet,
 *   isBelowDesktop,
 *   mobileSlice,
 *   tabletSlice,
 *   chartContainerRef,
 *   handleSliceClick,
 *   clearMobileSlice,
 * } = useDonutTouchInteraction<ChartDataType>()
 *
 * // On Pie component:
 * <Pie
 *   activeShape={isBelowDesktop ? renderStaticActiveShape : renderActiveShape}
 *   onClick={(_, index) => {
 *     const slice = chartData[index]
 *     if (slice) handleSliceClick(slice)
 *   }}
 * />
 *
 * // Disable RechartsHoverAdapter on touch devices:
 * <RechartsHoverAdapter disabled={isBelowDesktop}>
 * ```
 */
export function useDonutTouchInteraction<T>(): DonutTouchInteractionResult<T> {
  const isMobile = useIsMobile()
  const isBelowDesktop = useIsBelowDesktop()
  const isTablet = isBelowDesktop && !isMobile

  const [mobileSlice, setMobileSlice] = useState<T | null>(null)
  const [tabletSlice, setTabletSlice] = useState<T | null>(null)
  const [tabletAnchor, setTabletAnchor] = useState<AnchorPosition | null>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  // Handle click on pie slice
  const handleSliceClick = useCallback(
    (slice: T) => {
      if (isMobile) {
        setMobileSlice(slice)
      } else if (isTablet) {
        setTabletSlice(slice)
        // Calculate anchor position from ref in event handler (safe to access ref here)
        if (chartContainerRef.current) {
          const rect = chartContainerRef.current.getBoundingClientRect()
          setTabletAnchor({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          })
        }
      }
    },
    [isMobile, isTablet]
  )

  const clearMobileSlice = useCallback(() => setMobileSlice(null), [])
  const clearTabletSlice = useCallback(() => {
    setTabletSlice(null)
    setTabletAnchor(null)
  }, [])

  // Close tablet popover on any click (next click after opening)
  useEffect(() => {
    if (!isTablet || !tabletSlice) return

    const handleAnyClick = (event: MouseEvent): void => {
      // Ignore clicks inside the hovercard itself
      const target = event.target as HTMLElement | null
      if (target && target.closest('[data-anchored-hover-card="true"]')) return

      // Close the popover on outside click
      // If user clicks a different slice, handleSliceClick will immediately set new state
      setTabletSlice(null)
      setTabletAnchor(null)
    }

    // Use timeout to avoid closing immediately on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleAnyClick)
    }, 0)

    return (): void => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleAnyClick)
    }
  }, [isTablet, tabletSlice])

  return {
    isMobile,
    isTablet,
    isBelowDesktop,
    mobileSlice,
    tabletSlice,
    tabletAnchor,
    chartContainerRef,
    handleSliceClick,
    clearMobileSlice,
    clearTabletSlice,
  }
}
